/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import { Route, Routes, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import useScrollTop from 'core/hooks/useScrollTop';
import { SECTIONS } from 'constants/sections';
import { PasswordType } from './components/Password';
import { MANAGE_PANELS } from './pages/Manage/Sidebar';
import { FlowPage } from 'core/slices/flow';
import { CourseTabs } from './pages/Create/Courses/Course/Users';
import { LoadingSpinner } from './components/Loading/loadingspinner';

// Code-split imports for current (v2) routes
const ProductPage = lazy(() => import('./pagesV2/product/ProductPage'));
const LearnerPage = lazy(() => import('./pagesV2/learner/LearnerPage'));
const ModuleSharePage = lazy(() => import('./pagesV2/modules/Share/ModuleSharePage'));
const PagesV2 = lazy(() => import('./pagesV2/Home'));
const ModulesPage = lazy(() => import('./pagesV2/modules/ModulesPage'));
const CoursePage = lazy(() => import('./pagesV2/course/CoursePage'));
const AdminBoundary = lazy(() => import('./features/AdminBoundary/AdminBoundary'));
const WorkFlowPage = lazy(() => import('./pagesV2/course/WorkFlow/WorkFlowPage'));
const CourseLearnersPage = lazy(() => import('./pagesV2/course/Learners/CourseLearners'));
const InviteCourseLearners = lazy(() => import('./pagesV2/course/Invites/InviteCourseLearners'));
const InviteCourseLearnerRemindersPage = lazy(() => import('./pagesV2/course/Invites/InviteReminders'));
const Integrations = lazy(() => import('./pagesV2/course/Integrations/CourseIntegrations'));
const IntegrationEdit = lazy(() => import('./pagesV2/course/Integrations/Edit/CourseIntegrationEdit'));
const Certificates = lazy(() => import('./pagesV2/course/Certificates/Certificates'));
const NewModulePage = lazy(() => import('./pagesV2/modules/New/NewModulePage'));
const NewModuleTypePage = lazy(() => import('./pagesV2/modules/New/Type/NewModuleTypePage'));
const ModuleImportPage = lazy(() => import('./pagesV2/modules/New/Import/ModuleImportPage'));
const LearnerProgressPage = lazy(() => import('./pagesV2/course/Learner/LearnerProgressPage'));
const LearnerEmailPage = lazy(() => import('./pagesV2/learner/Email/LearnerEmailPage'));
const LearnerVariablePage = lazy(() => import('./pagesV2/learner/Variable/LearnerVariablePage'));
const CoursesPage = lazy(() => import('./pagesV2/courses/CoursesPage'));
const NewCoursePage = lazy(() => import('./pagesV2/courses/New/NewCoursePage'));
const ModuleViewPage = lazy(() => import('./pagesV2/modules/View/ModuleViewPage'));
const ModuleEditPage = lazy(() => import('./pagesV2/modules/Edit/ModuleEditPage'));
const ThemePage = lazy(() => import('./pagesV2/settings/theme/ThemePage'));
const LearnersPage = lazy(() => import('./pagesV2/learners/LearnersPage'));
const ReportsPage = lazy(() => import('./pagesV2/reports/ReportsPage'));
const VariableReportPage = lazy(() => import('./pagesV2/course/Integrations/Report/VariableReportPage'));

// Code-split imports for v1 routes (legacy)
const Product = lazy(() => import('./pages/Product/Loadable').then(m => ({ default: m.Product })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage/Loadable').then(m => ({ default: m.NotFoundPage })));
const Course = lazy(() => import('./pages/Create/Courses/Course/Loadable').then(m => ({ default: m.Course })));
const Courses = lazy(() => import('./pages/Create/Courses/Loadable').then(m => ({ default: m.Courses })));
const Module = lazy(() => import('./pages/Create/Modules/Module/Loadable').then(m => ({ default: m.Module })));
const Modules = lazy(() => import('./pages/Create/Modules/Loadable').then(m => ({ default: m.Modules })));
const Preview = lazy(() => import('./pages/Create/Preview/Loadable').then(m => ({ default: m.Preview })));
const Group = lazy(() => import('./pages/Manage/Groups/Group/Loadable').then(m => ({ default: m.Group })));
const ActionEditor = lazy(() => import('./components/ActionEdit/Loadable').then(m => ({ default: m.ActionEditor })));
const LogIn = lazy(() => import('./components/LogIn/Loadable').then(m => ({ default: m.LogIn })));
const Create = lazy(() => import('./pages/Create/Loadable').then(m => ({ default: m.Create })));
const Manage = lazy(() => import('./pages/Manage/Loadable').then(m => ({ default: m.Manage })));
const Admin = lazy(() => import('./pages/Admin/Loadable').then(m => ({ default: m.Admin })));
const Password = lazy(() => import('./components/Password/Loadable').then(m => ({ default: m.Password })));
const Users = lazy(() => import('./pages/Manage/Users/Loadable').then(m => ({ default: m.Users })));
const Groups = lazy(() => import('./pages/Manage/Groups/Loadable').then(m => ({ default: m.Groups })));
const ForgotPassword = lazy(() => import('./components/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const CourseUsers = lazy(() =>
  import('./pages/Create/Courses/Course/Users/Loadable').then(m => ({ default: m.CourseUsers })),
);
const Theme = lazy(() => import('./pages/Manage/Theme/Loadable').then(m => ({ default: m.Theme })));
const SignUp = lazy(() => import('./components/SignUp/Loadable').then(m => ({ default: m.SignUp })));
const New = lazy(() => import('./pages/New/Loadable').then(m => ({ default: m.New })));
const Home = lazy(() => import('./pages/Home/Loadable').then(m => ({ default: m.Home })));
const Systems = lazy(() => import('./pages/Manage/Systems/Loadable').then(m => ({ default: m.Systems })));
const Variables = lazy(() => import('./pages/Manage/Variables/Loadable').then(m => ({ default: m.Variables })));
const Dashboard = lazy(() => import('./pages/Manage/Dashboard').then(m => ({ default: m.Dashboard })));

const router = createBrowserRouter([{ path: '*', Component: Root }]);

export function App() {
  return (
    <>
      <RouterProvider router={router} />
      <video id="videoEl" className="hide-video" playsInline muted webkit-playsinline="true" />
    </>
  );
}

export function Root() {
  useTranslation();
  useScrollTop();
  return (
    <Suspense
      fallback={
        <div className="w-full h-full flex justify-center items-center">
          <LoadingSpinner />
        </div>
      }
    >
      <Routes>
        <Route path={`en/:token`} element={<ProductPage />} />
        <Route path={`scorm/:lang/:token`} element={<ProductPage scorm={true} />} />
        <Route path={`en/:token/:entityToken`} element={<ProductPage />} />
        <Route path={`invite/:inviteToken`} element={<ProductPage />} />
        <Route path={`sso/:inviteToken/:token`} element={<ProductPage />} />
        <Route path={`invite/:inviteToken/:entityToken`} element={<ProductPage />} />
        <Route path={`token/:token/:entityToken`} element={<ProductPage />} />
        {/* Current version routes (modern pages) */}
        <Route path={'/'} element={<AdminBoundary />}>
          <Route index={true} element={<PagesV2 />} />
          <Route path={SECTIONS.MODULES}>
            <Route index={true} element={<ModulesPage isHome />} />
            <Route path={`new`} element={<NewModulePage />} />
            <Route path={`new/import`} element={<ModuleImportPage />} />
            <Route path={`new/:type`} element={<NewModuleTypePage />} />
          </Route>
          <Route path={SECTIONS.MODULE}></Route>
          <Route path={SECTIONS.COURSES} element={<CoursesPage />} />
          <Route path={`${SECTIONS.COURSE}/new`} element={<NewCoursePage />} />
          <Route path={SECTIONS.LEARNERS} element={<LearnersPage />} />
          <Route path={SECTIONS.VARIABLES} element={<ReportsPage />} />
          <Route path={`${SECTIONS.LEARNER}/:userId`} element={<LearnerPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId`} element={<CoursePage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}`} element={<ModulesPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/new`} element={<NewModulePage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/new/import`} element={<ModuleImportPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/new/:type`} element={<NewModuleTypePage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/:moduleId`} element={<ModuleEditPage />} />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.COURSE_NODE}/:courseNodeId/share`}
            element={<ModuleSharePage />}
          />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/:moduleId/preview`}
            element={<ModuleViewPage />}
          />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.WORKFLOW}`} element={<WorkFlowPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.LEARNERS}`} element={<CourseLearnersPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INVITES}`} element={<CourseLearnersPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INVITES}/new`} element={<InviteCourseLearners />} />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INVITES}/reminders`}
            element={<InviteCourseLearnerRemindersPage />}
          />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.LEARNERS}/progress/:courseProgressId`}>
            <Route index={true} element={<LearnerProgressPage />} />
            <Route path={`email/:emailId`} element={<LearnerEmailPage />}></Route>
            <Route path={`variable/:orgUserVariableId`} element={<LearnerVariablePage />}></Route>
          </Route>
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INTEGRATIONS}`} element={<Integrations />} />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INTEGRATIONS}/:orgSystemId/edit`}
            element={<IntegrationEdit />}
          />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.VARIABLES}/:variableId`}
            element={<VariableReportPage />}
          />
          <Route path={`${SECTIONS.COURSE}/:courseId/certificates`} element={<Certificates />} />
          <Route path={SECTIONS.THEME} element={<ThemePage />} />
        </Route>

        {/* Legacy v1 routes */}
        <Route path={'/v1'} element={<Admin />}>
          <Route
            index={true}
            element={
              <Create section={SECTIONS.MODULES}>
                <CourseUsers />
              </Create>
            }
          ></Route>

          <Route path={'/v1/dashboard'}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.MODULES}>
                  <Dashboard></Dashboard>
                </Create>
              }
            ></Route>
          </Route>

          <Route path={`/v1/${SECTIONS.MODULES}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.MODULES}>
                  <Courses />
                </Create>
              }
            ></Route>
          </Route>

          <Route path={`/v1/${SECTIONS.WORKSPACE}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.WORKSPACE}>
                  <CourseUsers />
                </Create>
              }
            ></Route>
          </Route>
          <Route path={`/v1/${SECTIONS.GROUPS}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.GROUPS}>
                  <Groups />
                </Create>
              }
            ></Route>
          </Route>

          <Route path={`/v1/${SECTIONS.SYSTEMS}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.SYSTEMS}>
                  <Systems />
                </Create>
              }
            ></Route>
          </Route>
          <Route path={`/v1/${SECTIONS.CERTIFICATES}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.CERTIFICATES}>
                  <Certificates />
                </Create>
              }
            ></Route>
          </Route>
          <Route path={`/v1/${SECTIONS.VARIABLES}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.VARIABLES}>
                  <Variables />
                </Create>
              }
            ></Route>
            <Route
              path={`:variableId`}
              element={
                <Create section={SECTIONS.VARIABLES}>
                  <Variables />
                </Create>
              }
            />
          </Route>
          <Route path={`/v1/${SECTIONS.ADMINISTRATORS}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.ADMINISTRATORS}>
                  <Users />
                </Create>
              }
            ></Route>
          </Route>
          <Route path={`/v1/${SECTIONS.THEME}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.THEME}>
                  <Theme />
                </Create>
              }
            ></Route>
          </Route>

          {/* Legacy v1 module routes */}
          <Route path={'/v1/story'}>
            <Route path={`:moduleId`} element={<Module />} />
            <Route path={`:moduleId/preview`} element={<Preview />} />
            <Route path={`:moduleId/:actionId`} element={<Preview edit={true} />} />
            <Route path={`:moduleId/:actionId/preview`} element={<Preview />} />
            <Route path={`:moduleId/s/:sceneId`} element={<ActionEditor />} />
          </Route>
          <Route path={'/v1/module'}>
            <Route path={`:moduleId`} element={<Module />} />
            <Route path={`:moduleId/preview`} element={<Preview />} />
            <Route path={`:moduleId/:actionId`} element={<Preview edit={true} />} />
            <Route path={`:moduleId/:actionId/preview`} element={<Preview />} />
            <Route path={`:moduleId/s/:sceneId`} element={<ActionEditor />} />
          </Route>

          <Route path={`/v1/${SECTIONS.COURSES}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.COURSES}>
                  <Courses isDefault={true} />
                </Create>
              }
            ></Route>
          </Route>

          <Route path={`/v1/${SECTIONS.COURSE}`}>
            <Route
              index={true}
              element={
                <Create section={SECTIONS.COURSES}>
                  <Courses isDefault={true} />
                </Create>
              }
            />
            <Route path={`:courseId`} element={<CourseUsers />} />
            <Route path={`:courseId/workflow`} element={<Course />} />
            <Route path={`:courseId/modules`} element={<CourseUsers tab={CourseTabs.MODULES} />} />
            <Route path={`:courseId/learners`} element={<CourseUsers tab={CourseTabs.LEARNERS} />} />
            <Route path={`:courseId/records`} element={<CourseUsers tab={CourseTabs.RECORDS} />} />
            <Route path={`:courseId/notification`} element={<CourseUsers tab={CourseTabs.NOTIFICATION} />} />
            <Route path={`:courseId/publish`} element={<CourseUsers tab={CourseTabs.PUBLISH} />} />
            <Route path={`:courseId/editors`} element={<CourseUsers tab={CourseTabs.EDITORS} />} />
            <Route path={`:courseId/:nodeId`} element={<Course />} />
            <Route path={`:courseId/preview`} element={<Preview />} />
            <Route path={`:courseId/:nodeId/preview`} element={<Preview />} />
            <Route path={`:courseId/module/`}>
              <Route path={`:moduleId`} element={<Module page={FlowPage.MOD_CAROUSEL} />} />
              <Route path={`:moduleId/cards`} element={<Module page={FlowPage.MOD_CAROUSEL} />} />
              <Route path={`:moduleId/cards/preview`} element={<Preview page={FlowPage.MOD_CAROUSEL} />} />
              <Route
                path={`:moduleId/cards/:actionId`}
                element={<Preview page={FlowPage.MOD_CAROUSEL} edit={true} />}
              />
              <Route path={`:moduleId/cards/:actionId/preview`} element={<Preview page={FlowPage.MOD_CAROUSEL} />} />
              <Route path={`:moduleId/storyboard`} element={<Module page={FlowPage.MOD_FLOW} />} />
              <Route path={`:moduleId/storyboard/preview`} element={<Preview page={FlowPage.MOD_FLOW} />} />
              <Route
                path={`:moduleId/storyboard/:actionId`}
                element={<Preview page={FlowPage.MOD_FLOW} edit={true} />}
              />
              <Route path={`:moduleId/storyboard/:actionId/preview`} element={<Preview page={FlowPage.MOD_FLOW} />} />
              <Route path={`:moduleId/preview`} element={<Preview />} />
              <Route path={`:moduleId/:actionId`} element={<Preview edit={true} />} />
              <Route path={`:moduleId/:actionId/preview`} element={<Preview />} />
              <Route path={`:moduleId/s/:sceneId`} element={<ActionEditor />} />
            </Route>
            <Route path={`:courseId/scene/:sceneId`} element={<ActionEditor />} />
          </Route>

          <Route
            path={'/v1/folders'}
            element={
              <Create section={SECTIONS.MODULES}>
                <Modules />
              </Create>
            }
          />

          <Route path={'/v1/theme'}>
            <Route
              index={true}
              element={
                <Manage panel={MANAGE_PANELS.OVERVIEW}>
                  <Theme />
                </Manage>
              }
            />
          </Route>

          <Route path={'/v1/admin'}>
            <Route
              index={true}
              element={
                <Manage panel={MANAGE_PANELS.USERS}>
                  <Users />
                </Manage>
              }
            />
            <Route
              path={'groups'}
              element={
                <Manage panel={MANAGE_PANELS.GROUPS}>
                  <Groups />
                </Manage>
              }
            />
            <Route
              path={'group/:groupId'}
              element={
                <Manage panel={MANAGE_PANELS.GROUPS}>
                  <Group />
                </Manage>
              }
            />
          </Route>
        </Route>

        {/* Public routes and special routes that don't need versioning */}
        <Route path={'home'} element={<Home />} />
        <Route path={'manager'} element={<LogIn />} />

        {/* Product access routes */}
        <Route path={`go/:token`} element={<Product />} />
        <Route path={`token/:token`} element={<Product />} />
        <Route path={`go/:token/:start`} element={<Product />} />
        <Route path={`en/:token`} element={<Product />} />
        <Route path={`scorm/:lang/:token`} element={<Product scorm={true} />} />
        <Route path={`en/:token/:entityToken`} element={<Product />} />
        <Route path={`invite/:inviteToken`} element={<Product />} />
        <Route path={`sso/:inviteToken/:token`} element={<Product useToken={true} />} />
        <Route path={`invite/:inviteToken/:entityToken`} element={<Product useToken={true} />} />
        <Route path={`token/:token/:entityToken`} element={<Product />} />

        {/* Authentication routes */}
        <Route path={`hello/:token`} element={<Password type={PasswordType.RESET} />} />
        <Route path={`hello/:token/:returnToken`} element={<Password type={PasswordType.RESET} />} />
        <Route path={`reset/:token`} element={<Password type={PasswordType.RESET} />} />
        <Route path={`forgot`} element={<ForgotPassword />} />
        <Route path={`signup`} element={<SignUp />} />
        <Route path={'new/:token'} element={<New />} />
        <Route path={'new/:token/:courseId/:moduleId'} element={<New />} />

        {/* 404 fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
